import React from "react";
import styled from "@emotion/styled";
import { borderRadius, fontWeight, fontSize, spacing, dataChipColors } from "@atlas-int/tokens";

export interface DataChipStyles {
  color: string;
  backgroundColor: string;
  stroke?: string;
}

export interface DataChipProps {
  label: string;
  chipType: "dataType" | "classification";
  size?: "small" | "medium";
  onClick?: () => void;
}

const defaultStyle: DataChipStyles = {
  color: dataChipColors.dataType.default.color,
  backgroundColor: dataChipColors.dataType.default.bg,
};

/**
 * Canonical label for classification chips (style lookup + display).
 * Maps HS product-class strings (e.g. hs22, HS22) to HS22; trims whitespace.
 */
export const normalizeClassificationChipLabel = (raw: string): string => {
  const t = raw.trim();
  const hs = /^hs(\d{2})$/i.exec(t);
  if (hs) return `HS${hs[1]}`;
  if (/^sitc$/i.test(t)) return "SITC";
  return t;
};

export const formatDataTypeLabel = (type: string): string => {
  const specialCases: Record<string, string> = {
    product_space: "Product Space",
    bilateral: "Bilateral Trade",
    unilateral: "Unilateral Trade",
    product: "Product Trade",
    rankings: "Rankings",
    classification: "Classifications",
  };

  if (specialCases[type.toLowerCase()]) {
    return specialCases[type.toLowerCase()];
  }

  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const getDataTypeStyle = (type: string): DataChipStyles => {
  const normalizedType = type.toLowerCase();

  const styles: Record<string, DataChipStyles> = {
    rankings: {
      color: dataChipColors.dataType.rankings.color,
      backgroundColor: dataChipColors.dataType.rankings.bg,
    },
    bilateral: {
      color: dataChipColors.dataType.bilateral.color,
      backgroundColor: dataChipColors.dataType.bilateral.bg,
    },
    unilateral: {
      color: dataChipColors.dataType.unilateral.color,
      backgroundColor: dataChipColors.dataType.unilateral.bg,
    },
    product: {
      color: dataChipColors.dataType.product.color,
      backgroundColor: dataChipColors.dataType.product.bg,
    },
    classification: {
      color: dataChipColors.dataType.classification.color,
      backgroundColor: dataChipColors.dataType.classification.bg,
    },
    product_space: {
      color: dataChipColors.dataType.productSpace.color,
      backgroundColor: dataChipColors.dataType.productSpace.bg,
    },
    "product repo": {
      color: dataChipColors.dataType.productRepo.color,
      backgroundColor: dataChipColors.dataType.productRepo.bg,
    },
  };

  return styles[normalizedType] || defaultStyle;
};

export const getClassificationStyle = (type: string): DataChipStyles => {
  const key = normalizeClassificationChipLabel(type);
  const styles: Record<string, DataChipStyles> = {
    HS92: {
      color: dataChipColors.classification.HS92.color,
      backgroundColor: dataChipColors.classification.HS92.bg,
      stroke: dataChipColors.classification.HS92.stroke,
    },
    HS12: {
      color: dataChipColors.classification.HS12.color,
      backgroundColor: dataChipColors.classification.HS12.bg,
      stroke: dataChipColors.classification.HS12.stroke,
    },
    HS22: {
      color: dataChipColors.classification.HS22.color,
      backgroundColor: dataChipColors.classification.HS22.bg,
      stroke: dataChipColors.classification.HS22.stroke,
    },
    SITC: {
      color: dataChipColors.classification.SITC.color,
      backgroundColor: dataChipColors.classification.SITC.bg,
      stroke: dataChipColors.classification.SITC.stroke,
    },
    "Unilateral Services": {
      color: dataChipColors.classification.Services.color,
      backgroundColor: dataChipColors.classification.Services.bg,
      stroke: dataChipColors.classification.Services.stroke,
    },
    "Services Unilateral": {
      color: dataChipColors.classification.Services.color,
      backgroundColor: dataChipColors.classification.Services.bg,
      stroke: dataChipColors.classification.Services.stroke,
    },
    Services: {
      color: dataChipColors.classification.Services.color,
      backgroundColor: dataChipColors.classification.Services.bg,
      stroke: dataChipColors.classification.Services.stroke,
    },
  };
  return styles[key] || defaultStyle;
};

const ChipBase = styled.span<{
  $size: "small" | "medium";
  $clickable: boolean;
}>`
  display: inline-flex;
  align-items: center;
  height: ${({ $size }) => ($size === "small" ? "24px" : "32px")};
  padding: ${spacing[1]} ${spacing[4]};
  border-radius: ${borderRadius.base};
  font-weight: ${fontWeight.semibold};
  font-size: ${({ $size }) => ($size === "small" ? fontSize.sm : fontSize.base)};
  line-height: 1;
  white-space: nowrap;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  box-sizing: border-box;
`;

const FilledChip = styled(ChipBase)``;

const OutlinedChip = styled(ChipBase)`
  background-color: transparent;
  border: 1px solid ${dataChipColors.outlinedBorder};
`;

const DataChip: React.FC<DataChipProps> = ({
  label,
  chipType,
  size = "small",
  onClick,
}) => {
  const getStyle = () => {
    if (chipType === "dataType") {
      return getDataTypeStyle(label);
    } else {
      return getClassificationStyle(label);
    }
  };

  const style = getStyle();

  // Format the display label for data types
  const displayLabel =
    chipType === "dataType"
      ? formatDataTypeLabel(label)
      : normalizeClassificationChipLabel(label);

  if (chipType === "classification") {
    return (
      <OutlinedChip $size={size} $clickable={!!onClick} onClick={onClick}>
        <svg height="10" width="10" style={{ paddingRight: spacing[2], flexShrink: 0 }}>
          <rect
            height="100%"
            width="100%"
            fill={style.stroke || dataChipColors.outlinedBorder}
            rx="2"
          />
        </svg>
        {displayLabel}
      </OutlinedChip>
    );
  }

  return (
    <FilledChip
      $size={size}
      $clickable={!!onClick}
      onClick={onClick}
      style={{
        color: style.color,
        backgroundColor: style.backgroundColor,
      }}
    >
      {displayLabel}
    </FilledChip>
  );
};

export default DataChip;
